import React from 'react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import {
  Call,
  CallEnd,
  Mic,
  MicOff,
  VolumeUp,
  Hearing,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const ACCENT = '#2391FF';
/** Mismo tamaño que SoftphonePage (atender / mute / speaker / colgar). */
const BTN = 48;
const ICON = 24;

export interface DoorbellCallOverlayProps {
  visible: boolean;
  peer?: string;
  /** waiting = REGISTER en curso; ready = SIP listo, aún no tocó Atender; failed = timeout. */
  phase: 'ringing' | 'calling' | 'active' | 'ended' | 'idle' | 'waiting' | 'ready' | 'failed';
  muted: boolean;
  speaker: boolean;
  /** Accept en curso: spinner en el botón verde. */
  answering?: boolean;
  onAnswer?: () => void;
  onHangup?: () => void;
  onToggleMute?: () => void;
  onToggleSpeaker?: () => void;
}

/**
 * Controles de llamada SoftPhone sobre Video fullscreen (timbre).
 * Verde deshabilitado (gris + spinner) hasta REGISTER.
 * Listo (ready/ringing): se puede tocar Atender. Tras el toque: spinner en el verde.
 */
const DoorbellCallOverlay: React.FC<DoorbellCallOverlayProps> = ({
  visible,
  phase,
  muted,
  speaker,
  answering = false,
  onAnswer,
  onHangup,
  onToggleMute,
  onToggleSpeaker,
}) => {
  const { t } = useTranslation();
  if (!visible || phase === 'idle' || phase === 'ended') return null;

  const waiting = phase === 'waiting';
  const failed = phase === 'failed';
  const ringing = phase === 'ringing' || phase === 'ready';
  const inCall = phase === 'active' || phase === 'calling';
  const showAnswer = (waiting || ringing) && Boolean(onAnswer);
  const answerReady = ringing && !answering && !failed;
  const preparing = waiting && !failed;
  const connecting = ringing && answering;

  let answerAria = t('softphone.controls.answer') as string;
  if (preparing) answerAria = t('softphone.doorbell.answerNotReady') as string;
  else if (connecting) answerAria = t('softphone.doorbell.connecting') as string;

  return (
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: {
          xs: 'max(88px, calc(env(safe-area-inset-bottom) + 72px))',
          sm: 96,
        },
        zIndex: 10002,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        px: 2,
        pt: 1.5,
        pb: 1.5,
        background: 'linear-gradient(transparent, rgba(0,0,0,0.75))',
        pointerEvents: 'auto',
      }}
    >
      {failed ? (
        <Typography
          sx={{
            color: '#fff',
            fontSize: '0.9rem',
            fontWeight: 600,
            textAlign: 'center',
            maxWidth: 320,
          }}
        >
          {t('softphone.doorbell.handoffFailed')}
        </Typography>
      ) : null}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.25 }}>
        {showAnswer ? (
          <IconButton
            onClick={onAnswer}
            disabled={!answerReady}
            aria-label={answerAria}
            aria-busy={preparing || connecting}
            sx={{
              width: BTN,
              height: BTN,
              backgroundColor: answerReady || connecting ? '#2e7d32' : '#4a4a4a',
              color: '#fff',
              '&:hover': {
                backgroundColor: answerReady ? '#388e3c' : '#4a4a4a',
              },
              '&.Mui-disabled': {
                backgroundColor: connecting ? '#2e7d32' : '#4a4a4a',
                color: '#fff',
                opacity: connecting ? 0.92 : 0.72,
              },
              boxShadow: connecting
                ? `0 0 20px ${ACCENT}88`
                : answerReady
                  ? `0 0 16px ${ACCENT}55`
                  : 'none',
            }}
          >
            {preparing || connecting ? (
              <CircularProgress
                size={26}
                thickness={5}
                sx={{ color: preparing ? '#c8c8c8' : '#fff' }}
              />
            ) : (
              <Call sx={{ fontSize: ICON }} />
            )}
          </IconButton>
        ) : null}

        {inCall && (
          <>
            <IconButton
              onClick={onToggleMute}
              aria-label={t('softphone.controls.mute') as string}
              sx={{
                width: BTN,
                height: BTN,
                backgroundColor: muted ? '#c62828' : '#1a1a1a',
                color: '#fff',
                border: '1px solid #444',
                '&:hover': { backgroundColor: muted ? '#d32f2f' : '#2a2a2a' },
              }}
            >
              {muted ? <MicOff sx={{ fontSize: ICON }} /> : <Mic sx={{ fontSize: ICON }} />}
            </IconButton>
            <IconButton
              onClick={onToggleSpeaker}
              aria-label={t('softphone.controls.speaker') as string}
              sx={{
                width: BTN,
                height: BTN,
                backgroundColor: speaker ? ACCENT : '#1a1a1a',
                color: '#fff',
                border: '1px solid #444',
                '&:hover': { backgroundColor: speaker ? '#1a7ae0' : '#2a2a2a' },
              }}
            >
              {speaker ? <VolumeUp sx={{ fontSize: ICON }} /> : <Hearing sx={{ fontSize: ICON }} />}
            </IconButton>
          </>
        )}

        <IconButton
          onClick={onHangup}
          disabled={connecting}
          aria-label={t('softphone.controls.hangup') as string}
          sx={{
            width: BTN,
            height: BTN,
            backgroundColor: '#c62828',
            color: '#fff',
            '&:hover': { backgroundColor: '#d32f2f' },
            '&.Mui-disabled': {
              backgroundColor: '#c62828',
              color: '#fff',
              opacity: 0.5,
            },
          }}
        >
          <CallEnd sx={{ fontSize: ICON }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default DoorbellCallOverlay;
